import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FileText,
  LogOut,
  Menu,
  X,
  User,
  Home
} from 'lucide-react';

const MissionUserSidebar = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  // Récupérer les informations utilisateur du token
  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserInfo({
          nom: payload.nom || `${payload.prenom || ''} ${payload.nom || ''}`.trim() || 'Mission User',
          email: payload.email,
          role: payload.role || 'missionUser',
          fonction: payload.fonction
        });
      }
    } catch (error) {
      console.error('Erreur extraction token:', error);
      setUserInfo({ nom: 'Mission User', role: 'missionUser' });
    }
  }, []);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setIsOpen(false);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fermer automatiquement la sidebar à chaque changement de page
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Navigation items pour MissionUser (un seul lien)
  const missionUserNavigationItems = [
    {
      path: '/fiches-mission-user',
      label: 'Mes Missions',
      icon: FileText
    }
  ];

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setIsOpen(false);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      // Déconnexion par défaut
      localStorage.removeItem('token');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userInfo');
      window.location.href = '/';
    }
    closeSidebar();
  };

  return (
    <div>
      <style jsx>{`
        /* Variables CSS pour MissionUser */
        :root {
          --sidebar-width: 280px;
          --sidebar-bg: #ffffff;
          --sidebar-border: #e5e7eb;
          --sidebar-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          --primary-color: #3b82f6;
          --primary-hover: #2563eb;
          --primary-light: #eff6ff;
          --text-primary: #1f2937;
          --text-secondary: #6b7280;
          --text-muted: #9ca3af;
          --hover-bg: #f9fafb;
          --active-bg: #eff6ff;
          --border-radius: 12px;
          --transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          --header-gradient: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
          --logout-color: #dc2626;
          --logout-hover: #b91c1c;
          --logout-bg: #fef2f2;
        }

        * {
          box-sizing: border-box;
        }

        .sidebar-toggle {
          position: fixed;
          top: 20px;
          left: ${isOpen ? 'calc(var(--sidebar-width) + 20px)' : '20px'};
          z-index: 1001;
          background: var(--primary-color);
          color: white;
          border: none;
          padding: 12px;
          border-radius: var(--border-radius);
          box-shadow: var(--sidebar-shadow);
          cursor: pointer;
          transition: var(--transition);
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
        }

        @media (max-width: 768px) {
          .sidebar-toggle {
            left: 20px !important;
          }
        }

        .sidebar-toggle:hover {
          background: var(--primary-hover);
          transform: translateY(-2px);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }

        .sidebar-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          z-index: 998;
          opacity: 0;
          visibility: hidden;
          transition: var(--transition);
        }

        .sidebar-overlay.show {
          opacity: 1;
          visibility: visible;
        }

        .sidebar {
          position: fixed;
          top: 0;
          left: 0;
          width: var(--sidebar-width);
          height: 100vh;
          background: var(--sidebar-bg);
          border-right: 1px solid var(--sidebar-border);
          box-shadow: var(--sidebar-shadow);
          z-index: 999;
          display: flex;
          flex-direction: column;
          transition: var(--transition);
          overflow: hidden;
          transform: translateX(-100%);
        }

        .sidebar.show {
          transform: translateX(0);
        }

        .sidebar-header {
          padding: 24px 20px;
          background: var(--header-gradient);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          position: relative;
          overflow: hidden;
        }

        .sidebar-header::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -50%;
          width: 100px;
          height: 100px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          transform: translate(50%, 50%);
        }

        .sidebar-header::after {
          content: '';
          position: absolute;
          bottom: -25%;
          left: -25%;
          width: 60px;
          height: 60px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          transform: translate(-50%, -50%);
        }

        .sidebar-title {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 20px;
          font-weight: 700;
          color: white;
          margin: 0;
          position: relative;
          z-index: 1;
          line-height: 1.2;
        }

        .sidebar-title .header-icon {
          width: 32px;
          height: 32px;
          padding: 6px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .user-info-section {
          padding: 16px 20px;
          background: linear-gradient(to bottom, rgba(59, 130, 246, 0.05), transparent);
          border-bottom: 1px solid var(--sidebar-border);
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .user-avatar {
          width: 48px;
          height: 48px;
          background: var(--primary-light);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .user-avatar-icon {
          width: 24px;
          height: 24px;
          color: var(--primary-color);
        }

        .user-details {
          flex: 1;
          min-width: 0;
        }

        .user-name {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0 0 2px 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .user-role {
          font-size: 12px;
          color: var(--text-muted);
          margin: 0;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .role-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: var(--primary-light);
          color: var(--primary-color);
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
        }

        .sidebar-nav {
          flex: 1;
          padding: 20px 16px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: var(--sidebar-border) transparent;
        }

        .sidebar-nav::-webkit-scrollbar {
          width: 4px;
        }

        .sidebar-nav::-webkit-scrollbar-track {
          background: transparent;
        }

        .sidebar-nav::-webkit-scrollbar-thumb {
          background: var(--sidebar-border);
          border-radius: 2px;
        }

        .nav-section {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          width: 100%;
          text-align: left;
          border: none;
          background: transparent;
          color: var(--text-secondary);
          font-size: 14px;
          font-weight: 500;
          border-radius: 12px;
          cursor: pointer;
          transition: var(--transition);
          position: relative;
          overflow: hidden;
          font-family: inherit;
        }

        .nav-item::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
          background: var(--primary-color);
          transform: scaleY(0);
          transition: var(--transition);
          border-radius: 0 4px 4px 0;
        }

        .nav-item:hover {
          background: var(--hover-bg);
          color: var(--text-primary);
          transform: translateX(4px);
        }

        .nav-item:hover::before {
          transform: scaleY(0.6);
        }

        .nav-item:hover .nav-icon-wrapper {
          background: #dbeafe;
          transform: scale(1.05);
        }

        .nav-item:hover .nav-icon {
          transform: scale(1.1);
        }

        .nav-item.active {
          background: var(--active-bg);
          color: var(--primary-color);
          font-weight: 600;
          border-right: 4px solid var(--primary-color);
        }

        .nav-item.active::before {
          transform: scaleY(1);
        }

        .nav-item.active .nav-icon-wrapper {
          background: #dbeafe;
        }

        .nav-item.active .nav-icon {
          color: var(--primary-color);
        }

        .nav-item.active::after {
          content: '';
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          width: 8px;
          height: 8px;
          background: var(--primary-color);
          border-radius: 50%;
        }

        .nav-icon-wrapper {
          width: 32px;
          height: 32px;
          background: #f3f4f6;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: var(--transition);
          flex-shrink: 0;
        }

        .nav-icon {
          width: 18px;
          height: 18px;
          transition: var(--transition);
        }

        .info-box {
          margin: 16px 16px 0 16px;
          padding: 16px;
          background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%);
          border: 1px solid #dbeafe;
          border-radius: 12px;
        }

        .info-box-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
          color: var(--primary-color);
          margin: 0 0 8px 0;
        }

        .info-box-text {
          font-size: 12px;
          color: var(--text-secondary);
          line-height: 1.5;
          margin: 0;
        }

        .logout-section {
          padding: 16px;
          border-top: 1px solid var(--sidebar-border);
          background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.02));
        }

        .logout-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          width: 100%;
          text-align: left;
          border: none;
          background: transparent;
          color: var(--logout-color);
          font-size: 14px;
          font-weight: 500;
          border-radius: 12px;
          cursor: pointer;
          transition: var(--transition);
          position: relative;
          font-family: inherit;
        }

        .logout-btn:hover {
          background: var(--logout-bg);
          transform: translateY(-1px);
        }

        .logout-btn:hover .logout-icon-wrapper {
          background: #fecaca;
          transform: scale(1.05);
        }

        .logout-btn:hover .logout-icon {
          transform: scale(1.1) rotate(-5deg);
        }

        .logout-icon-wrapper {
          width: 32px;
          height: 32px;
          background: #fee2e2;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: var(--transition);
          flex-shrink: 0;
        }

        .logout-icon {
          width: 18px;
          height: 18px;
          transition: var(--transition);
        }

        @media (min-width: 769px) {
          .sidebar.show {
            transform: translateX(0);
          }
          
          .sidebar-overlay {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .sidebar-overlay.show {
            display: block;
          }
        }

        .nav-item:focus,
        .logout-btn:focus,
        .sidebar-toggle:focus {
          outline: 2px solid var(--primary-color);
          outline-offset: 2px;
        }

        @keyframes slideInLeft {
          from {
            transform: translateX(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .sidebar.show {
          animation: slideInLeft 0.3s ease-out;
        }

        .sidebar-overlay.show {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>

      <button
        className="sidebar-toggle"
        onClick={toggleSidebar}
        title={isOpen ? "Fermer le menu" : "Ouvrir le menu"}
      >
        {isOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {isMobile && (
        <div
          className={`sidebar-overlay ${isOpen ? 'show' : ''}`}
          onClick={closeSidebar}
        />
      )}

      <div className={`sidebar ${isOpen ? 'show' : ''}`}>
        <div className="sidebar-header">
          <h3 className="sidebar-title">
            <div className="header-icon">
              <FileText size={20} />
            </div>
            <div>Mission User</div>
          </h3>
        </div>

        

        <nav className="sidebar-nav">
          <div className="nav-section">
            {missionUserNavigationItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                >
                  <div className="nav-icon-wrapper">
                    <IconComponent className="nav-icon" />
                  </div>
                  {item.label}
                </button>
              );
            })}
          </div>

      
        </nav>

        <div className="logout-section">
          <button onClick={handleLogout} className="logout-btn">
            <div className="logout-icon-wrapper">
              <LogOut className="logout-icon" />
            </div>
            Déconnexion
          </button>
        </div>
      </div>
    </div>
  );
};

export default MissionUserSidebar;